import cleanCoords from "@turf/clean-coords";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import type { Feature, LineString, MultiLineString, Point } from "geojson";

/**
 * Options for safeNearestPointOnLine
 */
interface SafeNearestPointOnLineOptions {
  /** Units for distance calculation (default: 'kilometers') */
  units?: "meters" | "kilometers" | "miles";
  /** Whether to clean duplicate consecutive coordinates before processing (default: true) */
  cleanFirst?: boolean;
}

/**
 * A safe wrapper around @turf/nearest-point-on-line that handles edge cases:
 * - Cleans duplicate consecutive coordinates (optional, enabled by default)
 * - Returns null instead of throwing if the geometry is invalid
 *
 * @param line - LineString or MultiLineString feature to check
 * @param point - Point to find the nearest point on the line to
 * @param options - Optional configuration
 * @returns The nearest point feature, or null if the geometry is invalid
 */
export function safeNearestPointOnLine(
  line: Feature<LineString | MultiLineString> | LineString | MultiLineString,
  point: Feature<Point> | Point | number[],
  options: SafeNearestPointOnLineOptions = {}
): Feature<Point> | null {
  const { cleanFirst = true, units = "kilometers" } = options;

  try {
    // Normalize to Feature format
    let lineFeature: Feature<LineString | MultiLineString>;
    if (line.type === "Feature") {
      lineFeature = line as Feature<LineString | MultiLineString>;
    } else {
      lineFeature = { type: "Feature", properties: {}, geometry: line as LineString | MultiLineString };
    }

    // Clean coordinates if requested (may throw if geometry becomes invalid)
    if (cleanFirst) {
      lineFeature = cleanCoords(lineFeature) as Feature<LineString | MultiLineString>;
    }

    // Call nearestPointOnLine (will throw if line has < 2 points)
    return nearestPointOnLine(lineFeature, point, { units });
  } catch (error) {
    // Return null for any errors (invalid geometry, insufficient points, etc.)
    return null;
  }
}
